import type {
  FeedbackQuestionResponse,
  SurveyResponse,
} from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import { useToast } from '@lms/ui/use-toast';
import { ArrowDownIcon, ArrowUpIcon, DeleteIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { PageBox, PageHeader, PageTitle } from '@/components/admin/page-box';
import TakeSurveyForm from '@/features/scormplayer/components/take-survey-form';
import {
  removeQuestionFromSurveyAction,
  updateQuestionOrderInSurvey,
} from '@/features/survey/survey-admin-api';
import AddQuestionToSurveyForm from './add-question-to-survey';

// Ports features/survey/components/survey-edit.tsx. The Next server actions on
// the order/remove buttons are replaced with async calls that revalidate the
// survey via `onChanged` (SWR mutate); the before/after preview reuses the
// already-ported trainee `TakeSurveyForm` in preview mode (no `sessionId`).
export default function SurveyEditView({
  survey,
  questions,
  slug,
  onChanged,
}: {
  survey: SurveyResponse;
  questions: FeedbackQuestionResponse[];
  slug: string;
  onChanged: () => void;
}) {
  const t = useTranslations('survey');
  const { toast } = useToast();

  const moveQuestion = async (questionId: number, direction: number) => {
    const result = await updateQuestionOrderInSurvey(
      survey.id,
      questionId,
      direction,
    );
    if (result.ok) {
      onChanged();
    } else {
      toast({ variant: 'destructive', description: result.error });
    }
  };

  const removeFromSurvey = async (questionId: number) => {
    const result = await removeQuestionFromSurveyAction(survey.id, questionId);
    if (result.ok) {
      onChanged();
    } else {
      toast({ variant: 'destructive', description: result.error });
    }
  };

  return (
    <>
      <PageBox>
        <PageHeader backHref={`/${slug}/admin/surveys`}>
          <PageTitle>{survey.name}</PageTitle>
        </PageHeader>
        <h1 className="text-lg font-semibold">{t('preview')}</h1>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex max-w-screen-md flex-col gap-8 border bg-white p-8 shadow-md">
            <TakeSurveyForm survey={survey} position="before_course" />
          </div>
          <div className="flex max-w-screen-md flex-col gap-8 border bg-white p-8 shadow-md">
            <TakeSurveyForm survey={survey} position="after_course" />
          </div>
        </div>
      </PageBox>
      <PageBox>
        <PageHeader>
          <PageTitle>{t('question')}</PageTitle>
        </PageHeader>
        <div className="rounded-md border bg-white p-8 shadow-md">
          <Table className="mb-8">
            <TableHeader>
              <TableRow>
                <TableHead className="w-5"></TableHead>
                <TableHead>{t('name')}</TableHead>
                <TableHead className="w-5"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {survey.questions
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((question) => (
                  <TableRow key={question.id}>
                    <TableCell>
                      <div className="flex flex-row gap-2">
                        <Button
                          size="sm"
                          onClick={() => moveQuestion(question.id, -1)}
                        >
                          <ArrowUpIcon />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => moveQuestion(question.id, 1)}
                        >
                          <ArrowDownIcon />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/${slug}/admin/surveys/question/${question.id}`}
                      >
                        {question.description}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => removeFromSurvey(question.id)}
                      >
                        <DeleteIcon />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <AddQuestionToSurveyForm
            surveyId={survey.id}
            questions={questions.filter((e) =>
              survey.questions.every((d) => d.id !== e.id),
            )}
            onAdded={onChanged}
          />
        </div>
      </PageBox>
    </>
  );
}
